import React, { useMemo, useState, useRef, useContext, createContext, useEffect} from 'react';
import { createPortal } from 'react-dom';
import {
    Modal, Card, Text, BlockStack, InlineStack, Box, Divider,
    Badge, Checkbox, RadioButton, TextField, Banner,
    Button, ButtonGroup, Tooltip,
} from '@shopify/polaris';
import SmartProductImage from './SmartProductImage';
import { getPreviewFrontUrl, getPreviewImageUrls } from './previewImage';
import { buildBundles } from './pressingHelpers';
import './RefundModal.css';


// ---------- hover-zoom popup (portaled to body so the modal's scroll
// container doesn't clip it) ----------

const ZoomContext = createContext({ show: () => {}, hide: () => {} });

function ZoomableWrap({ src, children }) {
    const zoom = useContext(ZoomContext);
    if (!src) return children;
    return (
        <span
            style={{ display: 'inline-block', lineHeight: 0, cursor: 'zoom-in' }}
            onMouseEnter={(e) => zoom.show(src, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={() => zoom.hide()}
        >
            {children}
        </span>
    );
}

function ZoomPopup({ src, rect }) {
    const W = 360;
    const H = 360;
    let left = rect.right + 12;
    if (left + W > window.innerWidth - 8) {
        left = Math.max(8, rect.left - W - 12);
    }
    let top = rect.top + (rect.height / 2) - (H / 2);
    if (top + H > window.innerHeight - 8) top = window.innerHeight - H - 8;
    if (top < 8) top = 8;
    return (
        <div className="rm-zoom-popup" style={{ left, top, width: W, height: H }}>
            {/* SmartProductImage probes the pixels and applies a light-grey
              * background when the image is mostly white — so a white SP logo
              * doesn't render invisibly against the popup's white card. */}
            <SmartProductImage
                src={src}
                alt="Preview"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
        </div>
    );
}


// ---------- pure classifier (testable; also used by unit tests) ----------
//
// Reuses the canonical `buildBundles` helper from pressingHelpers — same logic
// the pick/press/pack portals and OrderDetail use. Bundles are detected via
// Shopify lineItemGroup.id (Pass 1a), _soccer_parent_line_item_key (Pass 1b),
// _soccer_line_item_group (Pass 1c), "Parent product" attribute (Pass 1d), and
// Broken-Order tag (Pass 1e). The legacy `_parent_sku` attribute is NOT used
// by any Soccer Post bundle pattern; relying on it (as this modal originally
// did) misses every real bundle.

export function classifyLineItems(order) {
    const nodes = ((order?.lineItems?.edges) || []).map(e => e?.node).filter(Boolean);
    const tags = order?.tags || [];

    const isOptional = n => (n?.customAttributes || []).some(
        a => a.key === '_soccer_club_product_distribution' && a.value === 'optional'
    );
    const remaining = n => Math.max(0, (n?.currentQuantity ?? n?.quantity ?? 0));

    const bundleGroups = buildBundles(nodes, { tags });

    const bundles = [];
    const standaloneOptionals = [];
    const standalones = [];

    for (const g of bundleGroups) {
        const { parent, children } = g;
        const parentRem = remaining(parent);

        if (children.length > 0) {
            const mand = children.filter(c => !isOptional(c) && remaining(c) > 0);
            const opt  = children.filter(c =>  isOptional(c) && remaining(c) > 0);

            if (parentRem > 0) {
                bundles.push({ parent, mandatories: mand, optionals: opt });
            } else {
                // Parent already refunded — surface remaining optional children
                // standalone so the operator can still refund them. Mandatory
                // orphans (parent refunded, mandatory child still present)
                // get hidden — the bundle was the only valid refund grouping.
                for (const o of opt) standaloneOptionals.push(o);
            }
        } else {
            // Solo line item — no bundle. Treat as standalone unless its own
            // _soccer_club_product_distribution flags it as optional (rare;
            // typically only true for split-out add-on lines).
            if (parentRem <= 0) continue;
            if (isOptional(parent)) standaloneOptionals.push(parent);
            else standalones.push(parent);
        }
    }

    return { bundles, standaloneOptionals, standalones };
}


// ---------- helpers ----------

const HIDDEN_ATTR_KEYS = new Set(['preview front', 'preview back', 'preview', 'just_preview', 'parent product']);

function priceOf(node) {
    const a = node?.originalUnitPriceSet?.shopMoney?.amount;
    return a ? Number(a) : 0;
}
function remainingQty(node) {
    return Math.max(0, (node.currentQuantity ?? node.quantity ?? 0));
}
function isShippedNode(order, node) {
    const status = (order?.displayFulfillmentStatus || '').toUpperCase();
    if (status !== 'FULFILLED' && status !== 'PARTIALLY_FULFILLED') return false;
    return remainingQty(node) < (node.quantity || 0);
}

// Customized preview (Team Portal / PPLR) overrides the raw product image — matches OrderDetail.
function imgUrlFor(node) {
    return getPreviewFrontUrl(node.customAttributes || []) || node.image?.url || null;
}

// Visible (non-internal) attrs only. Excludes Shopify-internal _-prefixed keys and preview/just_preview noise.
function visibleAttrs(node) {
    return (node?.customAttributes || []).filter(a =>
        !a.key.startsWith('_') && !HIDDEN_ATTR_KEYS.has(a.key.toLowerCase())
    );
}

function extractPlayerInfo(parent, children = []) {
    const parentAttrs = visibleAttrs(parent);
    const all = [...parentAttrs, ...children.flatMap(visibleAttrs)];
    const playerName = parentAttrs.find(a => {
        const k = a.key.toLowerCase();
        return k === 'player name' || k === 'player' || k.includes('name');
    })?.value;
    const playerNumber = all.find(a => {
        const k = a.key.toLowerCase();
        return k === 'number' || k === '#';
    })?.value;
    const pressedName = all.find(a => a.key.toLowerCase() === 'name')?.value;
    const otherAttrs = parentAttrs.filter(a => {
        const k = a.key.toLowerCase();
        return !k.includes('name') && k !== 'player' && k !== 'number' && k !== '#';
    });
    return { playerName, playerNumber, pressedName, otherAttrs };
}

function PlayerFooter({ info }) {
    const { playerName, playerNumber, pressedName, otherAttrs } = info;
    if (!playerName && !playerNumber && !pressedName && otherAttrs.length === 0) return null;
    return (
        <Box paddingBlockStart="200">
            <InlineStack gap="200" wrap blockAlign="center">
                {playerName && <Badge tone="info">{`👤 ${playerName}`}</Badge>}
                {pressedName && <Badge tone="info">{`Name: ${pressedName}`}</Badge>}
                {playerNumber && <Badge tone="info">{`#${playerNumber}`}</Badge>}
                {otherAttrs.map(a => (
                    <Badge key={a.key}>{`${a.key}: ${a.value}`}</Badge>
                ))}
            </InlineStack>
        </Box>
    );
}

function ProductImage({ node, size = 48 }) {
    const src = imgUrlFor(node);
    // `contain` so embellishment art (logos, numbers, decals) shows in full —
    // `cover` was cropping the design down to the center square.
    const style = {
        width: size, height: size, objectFit: 'contain',
        borderRadius: 4, border: '1px solid #e5e7eb',
        flexShrink: 0, background: '#fff', padding: 2,
    };
    if (src) return (
        <ZoomableWrap src={src}>
            <SmartProductImage src={src} alt="" style={style} />
        </ZoomableWrap>
    );
    return (
        <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5 }}>
            📦
        </div>
    );
}

// Bundle parent preview — renders Front + Back customizer previews side-by-side
// when available (Soccer Post Team Portal or PPLR-style customizer attrs).
// Falls back to the raw product image. Mirrors the layout used by OrderDetail.
function BundlePreview({ parent, children }) {
    const attrs = [
        ...(parent?.lineItemGroup?.customAttributes || []),
        ...(parent?.customAttributes || []),
        ...(children || []).flatMap(c => c?.customAttributes || []),
    ];
    const { front, back } = getPreviewImageUrls(attrs);
    if (!front && !back) {
        return <ProductImage node={parent} size={72} />;
    }
    return (
        <div className="rm-bundle-preview">
            {front && (
                <div className="rm-bundle-preview-box">
                    <ZoomableWrap src={front}>
                        <SmartProductImage src={front} alt="Front" className="rm-bundle-preview-img" />
                    </ZoomableWrap>
                    <div className="rm-bundle-preview-label">Front</div>
                </div>
            )}
            {back && (
                <div className="rm-bundle-preview-box">
                    <ZoomableWrap src={back}>
                        <SmartProductImage src={back} alt="Back" className="rm-bundle-preview-img" />
                    </ZoomableWrap>
                    <div className="rm-bundle-preview-label">Back</div>
                </div>
            )}
        </div>
    );
}

function QtyInput({ value, min = 1, max, disabled, onChange }) {
    // 2026-07-06: shrunk width + tightened padding to match the compact
    // AmountAdjuster row below it; matching border-radius so the Qty and
    // Amount input read as the same visual scale.
    return (
        <input
            type="number"
            min={min}
            max={max}
            value={value}
            disabled={disabled}
            onChange={e => onChange(parseInt(e.target.value || '0', 10))}
            style={{
                width: 56, padding: '4px 6px',
                border: '1px solid #c9cccf', borderRadius: 6,
                fontSize: 14, textAlign: 'right',
            }}
        />
    );
}

/** Per-line refund adjustment: mode dropdown + value input.
 *
 *  Modes:
 *    'full'        — refund at qty × unit_price (default; no input needed)
 *    'custom'      — refund exactly the value entered (absolute $)
 *    'percent_off' — refund at autoValue × (1 - value/100)
 *    'dollar_off'  — refund at autoValue − value
 *
 *  A single refund can mix modes across lines: refund one bundle in full,
 *  take 10% off another, take $5 off a third — all bundled into one Shopify
 *  refund with a REFUND_DISCREPANCY orderAdjustment for the delta. */
export function computeEffectiveAmount(autoValue, mode, value) {
    const v = Number(value);
    if (mode === 'custom')       return Math.max(0, isFinite(v) ? v : autoValue);
    if (mode === 'percent_off')  return Math.max(0, autoValue * (1 - (isFinite(v) ? v : 0) / 100));
    if (mode === 'dollar_off')   return Math.max(0, autoValue - (isFinite(v) ? v : 0));
    return autoValue;  // 'full' or unknown
}

function AmountAdjuster({ autoValue, adjust, disabled, onChange }) {
    const mode  = adjust?.mode  || 'full';
    const value = adjust?.value ?? '';
    const effective  = computeEffectiveAmount(autoValue, mode, value);
    const isOverride = mode !== 'full';

    // 2026-07-06 v2: no expand/collapse — always render segmented control +
    // input row so the layout never bounces on mode change. Full mode
    // renders an invisible placeholder for the input (visibility: hidden)
    // so its height is reserved. Segmented control uses Polaris ButtonGroup.
    const inputSuffix =
        mode === 'percent_off' ? '%' :
        mode === 'dollar_off'  ? '$ off' :
        mode === 'custom'      ? '$' : '';
    const inputStep = mode === 'percent_off' ? '0.1' : '0.01';
    const inputMax  = mode === 'percent_off' ? '100' : undefined;

    function pickMode(next) {
        onChange({ mode: next, value: next === 'full' ? '' : value });
    }

    return (
        <BlockStack gap="200" align="end">
            {/* Segmented mode selector — Polaris ButtonGroup, buttons render
                joined + equal-height (segmented) instead of loose chips. */}
            <ButtonGroup segmented>
                <Button pressed={mode === 'full'}        onClick={() => pickMode('full')}        disabled={disabled}>Full</Button>
                <Button pressed={mode === 'custom'}      onClick={() => pickMode('custom')}      disabled={disabled}>Custom $</Button>
                <Button pressed={mode === 'percent_off'} onClick={() => pickMode('percent_off')} disabled={disabled}>% off</Button>
                <Button pressed={mode === 'dollar_off'}  onClick={() => pickMode('dollar_off')}  disabled={disabled}>$ off</Button>
            </ButtonGroup>

            {/* Input + effective amount — height reserved even for Full mode
                (visibility: hidden on the input group) so switching modes
                doesn't shift the row vertically. */}
            <InlineStack gap="300" blockAlign="center">
                <div style={{ visibility: mode === 'full' ? 'hidden' : 'visible' }}>
                    <InlineStack gap="150" blockAlign="center">
                        <input
                            type="number"
                            min="0"
                            max={inputMax}
                            step={inputStep}
                            value={value}
                            disabled={disabled || mode === 'full'}
                            placeholder={mode === 'percent_off' ? '10' : mode === 'dollar_off' ? '5' : autoValue.toFixed(2)}
                            onChange={e => onChange({ mode, value: e.target.value })}
                            style={{
                                width: 88, padding: '8px 10px',
                                border: '1px solid #b45309',
                                borderRadius: 6, fontSize: 15, textAlign: 'right',
                                fontWeight: 600, color: '#b45309',
                                lineHeight: 1.2,
                            }}
                        />
                        <Text as="span" variant="bodyMd" fontWeight="bold">{inputSuffix}</Text>
                    </InlineStack>
                </div>
                <span
                    style={{
                        minWidth: 96,
                        textAlign: 'right',
                        fontSize: 17,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: isOverride ? '#b45309' : undefined,
                    }}
                >
                    {`$${effective.toFixed(2)}`}
                </span>
            </InlineStack>
        </BlockStack>
    );
}


// ---------- row components ----------

function BundleCard({ order, bundle, selections, setSelections }) {
    const { parent, mandatories, optionals } = bundle;
    const mandCap = mandatories.reduce((m, c) => Math.min(m, remainingQty(c)), remainingQty(parent));
    const parentSel = selections[parent.id] || { selected: false, qty: mandCap, restock: !isShippedNode(order, parent) };
    const checked = parentSel.selected;
    const qty = parentSel.qty;
    const autoRefundable = priceOf(parent) * qty;
    const refundable = computeEffectiveAmount(autoRefundable, parentSel.adjust?.mode, parentSel.adjust?.value);
    const playerInfo = extractPlayerInfo(parent, [...mandatories, ...optionals]);

    function setParentChecked(v) {
        const next = { ...selections };
        next[parent.id] = { ...parentSel, selected: v, qty: v ? mandCap : 0 };
        for (const c of mandatories) {
            next[c.id] = { selected: v, qty: v ? mandCap : 0, restock: !isShippedNode(order, c) };
        }
        for (const o of optionals) {
            const oqty = Math.min(remainingQty(o), mandCap);
            if (oqty > 0) {
                next[o.id] = { selected: v, qty: v ? oqty : 0, restock: !isShippedNode(order, o) };
            }
        }
        setSelections(next);
    }

    function setParentQty(v) {
        const capped = Math.max(1, Math.min(v, mandCap));
        const next = { ...selections, [parent.id]: { ...parentSel, qty: capped } };
        for (const c of mandatories) {
            next[c.id] = { ...(next[c.id] || {}), selected: true, qty: capped };
        }
        for (const o of optionals) {
            const cur = next[o.id] || { selected: true, restock: !isShippedNode(order, o) };
            const oqty = Math.min(remainingQty(o), capped);
            if (cur.selected) next[o.id] = { ...cur, qty: oqty };
        }
        setSelections(next);
    }

    function setParentRestock(v) {
        const next = { ...selections, [parent.id]: { ...parentSel, restock: v } };
        for (const c of mandatories) {
            next[c.id] = { ...(next[c.id] || { selected: checked, qty }), restock: v };
        }
        setSelections(next);
    }

    function toggleOptional(opt, v) {
        // Optionals refund independently of the parent: if the bundle parent
        // is selected, cap at parent's qty so the cascade stays consistent;
        // otherwise default to the optional's own remaining qty.
        const oqty = checked ? Math.min(remainingQty(opt), qty) : remainingQty(opt);
        const next = { ...selections, [opt.id]: { selected: v, qty: v ? oqty : 0, restock: !isShippedNode(order, opt) } };
        setSelections(next);
    }

    return (
        <Card>
            <BlockStack gap="300">
                {/* Parent header */}
                <InlineStack gap="300" align="space-between" blockAlign="start" wrap={false}>
                    <InlineStack gap="300" blockAlign="start" wrap={false}>
                        <Box paddingBlockStart="100">
                            <Checkbox
                                label=""
                                labelHidden
                                checked={checked}
                                onChange={setParentChecked}
                            />
                        </Box>
                        <BundlePreview parent={parent} children={[...mandatories, ...optionals]} />
                        <BlockStack gap="100">
                            <InlineStack gap="200" blockAlign="center" wrap>
                                <Text variant="bodyMd" fontWeight="semibold" as="span">{parent.title}</Text>
                                <Badge tone="info">Bundle</Badge>
                            </InlineStack>
                            {parent.sku && (
                                <Text variant="bodySm" tone="subdued" as="span">SKU: {parent.sku}</Text>
                            )}
                            {parent.variant?.title && parent.variant.title !== 'Default Title' && (
                                <Text variant="bodySm" tone="subdued" as="span">{parent.variant.title}</Text>
                            )}
                        </BlockStack>
                    </InlineStack>
                    <Box minWidth="180px">
                        <BlockStack gap="050" align="end">
                            <InlineStack gap="200" blockAlign="center">
                                <Text as="span" variant="bodySm" tone="subdued">Qty</Text>
                                <QtyInput value={qty} min={1} max={mandCap}
                                          disabled={!checked} onChange={setParentQty} />
                            </InlineStack>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {`Remaining ${mandCap} of ${parent.quantity}`}
                            </Text>
                            <AmountAdjuster
                                autoValue={autoRefundable}
                                adjust={parentSel.adjust}
                                disabled={!checked}
                                onChange={adjust => setSelections({ ...selections, [parent.id]: { ...parentSel, adjust } })}
                            />
                        </BlockStack>
                    </Box>
                </InlineStack>

                {/* Player metadata footer for the bundle (e.g. team-portal player name + number) */}
                <PlayerFooter info={playerInfo} />

                {/* Restock toggle for the parent + mandatories */}
                <Checkbox
                    label="Restock bundle items"
                    checked={parentSel.restock}
                    disabled={!checked}
                    onChange={setParentRestock}
                />

                {(mandatories.length > 0 || optionals.length > 0) && <Divider />}

                {/* Mandatory children — auto-cascade, no per-item toggle */}
                {mandatories.length > 0 && (
                    <BlockStack gap="200">
                        <Text variant="bodySm" tone="subdued" as="span" fontWeight="medium">
                            Included with bundle
                        </Text>
                        {mandatories.map(c => (
                            <InlineStack key={c.id} gap="300" blockAlign="center" wrap={false}>
                                <Box paddingInlineStart="600">
                                    <InlineStack gap="300" blockAlign="center">
                                        <ProductImage node={c} size={40} />
                                        <BlockStack gap="050">
                                            <Text variant="bodyMd" as="span">{c.title}</Text>
                                            <InlineStack gap="200">
                                                {c.sku && <Text variant="bodySm" tone="subdued" as="span">SKU: {c.sku}</Text>}
                                                {c.variant?.title && c.variant.title !== 'Default Title' && (
                                                    <Text variant="bodySm" tone="subdued" as="span">{c.variant.title}</Text>
                                                )}
                                            </InlineStack>
                                        </BlockStack>
                                    </InlineStack>
                                </Box>
                            </InlineStack>
                        ))}
                    </BlockStack>
                )}

                {/* Optional children — auto-cascade but operator can uncheck individually */}
                {optionals.length > 0 && (
                    <BlockStack gap="200">
                        <Text variant="bodySm" tone="subdued" as="span" fontWeight="medium">
                            Optional add-ons (uncheck any to keep)
                        </Text>
                        {optionals.map(o => {
                            const osel = selections[o.id] || { selected: false, qty: 0 };
                            return (
                                <InlineStack key={o.id} gap="300" blockAlign="center" wrap={false}>
                                    <Box paddingInlineStart="600">
                                        <InlineStack gap="300" blockAlign="center">
                                            <Checkbox
                                                label=""
                                                labelHidden
                                                checked={!!osel.selected}
                                                onChange={v => toggleOptional(o, v)}
                                            />
                                            <ProductImage node={o} size={40} />
                                            <BlockStack gap="050">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text variant="bodyMd" as="span">{o.title}</Text>
                                                    <Badge tone="attention">Optional</Badge>
                                                </InlineStack>
                                                <InlineStack gap="200">
                                                    {o.sku && <Text variant="bodySm" tone="subdued" as="span">SKU: {o.sku}</Text>}
                                                    {o.variant?.title && o.variant.title !== 'Default Title' && (
                                                        <Text variant="bodySm" tone="subdued" as="span">{o.variant.title}</Text>
                                                    )}
                                                    <Text variant="bodySm" tone="subdued" as="span">
                                                        Qty {osel.qty || 0} of {o.quantity}
                                                    </Text>
                                                </InlineStack>
                                            </BlockStack>
                                        </InlineStack>
                                    </Box>
                                </InlineStack>
                            );
                        })}
                    </BlockStack>
                )}
            </BlockStack>
        </Card>
    );
}

function ItemCard({ order, node, selections, setSelections, optionalLabel }) {
    const cap = remainingQty(node);
    const sel = selections[node.id] || { selected: false, qty: cap, restock: !isShippedNode(order, node) };
    function update(patch) { setSelections({ ...selections, [node.id]: { ...sel, ...patch } }); }
    const autoRefundable = priceOf(node) * sel.qty;
    const refundable = computeEffectiveAmount(autoRefundable, sel.adjust?.mode, sel.adjust?.value);
    const playerInfo = extractPlayerInfo(node, []);

    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack gap="300" align="space-between" blockAlign="start" wrap={false}>
                    <InlineStack gap="300" blockAlign="start" wrap={false}>
                        <Box paddingBlockStart="100">
                            <Checkbox
                                label=""
                                labelHidden
                                checked={sel.selected}
                                onChange={v => update({ selected: v, qty: v ? cap : 0 })}
                            />
                        </Box>
                        <ProductImage node={node} size={64} />
                        <BlockStack gap="100">
                            <InlineStack gap="200" blockAlign="center" wrap>
                                <Text variant="bodyMd" fontWeight="semibold" as="span">{node.title}</Text>
                                {optionalLabel && <Badge tone="attention">{optionalLabel}</Badge>}
                            </InlineStack>
                            {node.sku && (
                                <Text variant="bodySm" tone="subdued" as="span">SKU: {node.sku}</Text>
                            )}
                            {node.variant?.title && node.variant.title !== 'Default Title' && (
                                <Text variant="bodySm" tone="subdued" as="span">{node.variant.title}</Text>
                            )}
                        </BlockStack>
                    </InlineStack>
                    <Box minWidth="180px">
                        <BlockStack gap="050" align="end">
                            <InlineStack gap="200" blockAlign="center">
                                <Text as="span" variant="bodySm" tone="subdued">Qty</Text>
                                <QtyInput
                                    value={sel.qty}
                                    min={1}
                                    max={cap}
                                    disabled={!sel.selected}
                                    onChange={v => update({ qty: Math.max(1, Math.min(v, cap)) })}
                                />
                            </InlineStack>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {`Remaining ${cap} of ${node.quantity}`}
                            </Text>
                            <AmountAdjuster
                                autoValue={autoRefundable}
                                adjust={sel.adjust}
                                disabled={!sel.selected}
                                onChange={adjust => update({ adjust })}
                            />
                        </BlockStack>
                    </Box>
                </InlineStack>

                <PlayerFooter info={playerInfo} />

                <Checkbox
                    label="Restock"
                    checked={sel.restock}
                    disabled={!sel.selected}
                    onChange={v => update({ restock: v })}
                />
            </BlockStack>
        </Card>
    );
}


// ---------- main modal ----------

export default function RefundModal({ order: rawOrder, storeKey, onClose, onRefundIssued }) {
    // ── Edited orders (Eric, 2026-10-16) ─────────────────────────────────────────────────
    // A Team Portal / Cleverific edit is a $0 clone of the original; the customer's payment sits
    // on the ORIGINAL, which Shopify cancels with every line at quantity 0. So on an edited order:
    // price each $0 line from the matching line of the original (same SKU, same occurrence), refund
    // the money as an order-level refund on the original, and send a $0 line refund here so the
    // items are recorded as returned (and restocked when ticked). Lines that carry their own price
    // (an edit that charged extra) refund here as usual.
    const parentId = rawOrder?.replacement_parent_order_id ? String(rawOrder.replacement_parent_order_id).split('/').pop() : null;
    const parentName = rawOrder?.replacement_parent_name || null;
    const [parentOrder, setParentOrder] = useState(null);
    const [parentError, setParentError] = useState(null);
    useEffect(() => {
        if (!parentId) return undefined;
        let live = true;
        let tok = null;
        try { tok = localStorage.getItem('operations_token'); } catch (_) { tok = null; }
        fetch(`/api/orders/${encodeURIComponent('gid://shopify/Order/' + parentId)}?store=${encodeURIComponent(storeKey)}`,
              { credentials: 'include', headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(d => { if (live) setParentOrder(d); })
            .catch(e => { if (live) setParentError(e.message || 'failed'); });
        return () => { live = false; };
    }, [parentId, storeKey]);
    // The order the modal works on: the edited order with its $0 lines re-priced from the original.
    const order = useMemo(() => {
        if (!rawOrder || !parentOrder) return rawOrder;
        const parentNodes = ((parentOrder.lineItems?.edges) || []).map(e => e?.node).filter(Boolean);
        const used = new Set();
        const edges = (rawOrder.lineItems?.edges || []).map(e => {
            const n = e?.node; if (!n) return e;
            const own = Number(n.originalUnitPriceSet?.shopMoney?.amount || 0);
            if (own > 0) return e;
            const sku = (n.sku || '').trim().toUpperCase();
            const match = parentNodes.find(pn => !used.has(pn.id) && (pn.sku || '').trim().toUpperCase() === sku
                                                 && Number(pn.originalUnitPriceSet?.shopMoney?.amount || 0) > 0);
            if (!match) return e;
            used.add(match.id);
            const amt = match.originalUnitPriceSet.shopMoney.amount;
            return { ...e, node: { ...n, _crossPrice: Number(amt), _crossFrom: parentName,
                                   originalUnitPriceSet: { shopMoney: { amount: amt, currencyCode: match.originalUnitPriceSet.shopMoney.currencyCode || 'USD' } } } };
        });
        return { ...rawOrder, lineItems: { ...rawOrder.lineItems, edges } };
    }, [rawOrder, parentOrder, parentName]);
    const { bundles, standaloneOptionals, standalones } = useMemo(
        () => classifyLineItems(order), [order]
    );
    const [selections, setSelections] = useState({});

    const [shippingChecked, setShippingChecked] = useState(false);
    const [shippingAmount, setShippingAmount] = useState(() => {
        const total = Number(order?.totalShippingPriceSet?.shopMoney?.amount || 0);
        const refunded = Number(order?.totalRefundedShippingSet?.shopMoney?.amount || 0);
        return Math.max(0, total - refunded).toFixed(2);
    });
    // Order-level refund: a discretionary amount not tied to any line item.
    const [orderRefundChecked, setOrderRefundChecked] = useState(false);
    const [orderRefundAmount, setOrderRefundAmount] = useState('0.00');
    // Club Discount: when on, force reason to "Club Discount" and tag the order.
    const [clubDiscount, setClubDiscount] = useState(false);
    // 2026-10-28 (Eric; Karen, Dallas Texans SP-322869): take the ticked items off the order
    // (restocked when Restock is ticked) and refund no money — Shopify admin's "Refund $0.00".
    const [itemsOnly, setItemsOnly] = useState(false);
    const priorReasonRef = useRef('');

    const [destination, setDestination] = useState('original_payment');
    const [notifyCustomer, setNotifyCustomer] = useState(false);
    const [reason, setReason] = useState('');

    // Checking "Club Discount" stamps the reason and locks the box; unchecking
    // restores whatever the operator had typed before.
    const toggleClubDiscount = (checked) => {
        setClubDiscount(checked);
        if (checked) {
            priorReasonRef.current = reason;
            setReason('Club Discount');
        } else {
            setReason(priorReasonRef.current || '');
        }
    };

    const customerHasAccount = (order?.customer?.state === 'ENABLED');

    const [zoom, setZoom] = useState(null);   // { src, rect }
    const zoomCtx = useMemo(() => ({
        show: (src, rect) => setZoom({ src, rect }),
        hide: () => setZoom(null),
    }), []);

    const [submitting, setSubmitting] = useState(false);
    const [errorBanner, setErrorBanner] = useState(null);
    const [idempotencyKey] = useState(() =>
        (window.crypto?.randomUUID?.() ||
         'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
             const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
         })));

    async function handleSubmit() {
        setSubmitting(true);
        setErrorBanner(null);
        const nodeById = {};
        for (const e of (order?.lineItems?.edges || [])) if (e?.node) nodeById[e.node.id] = e.node;
        let crossTotal = 0;
        const crossSummary = [];
        const line_items = Object.entries(selections)
            .filter(([, s]) => s.selected && s.qty > 0)
            .map(([lid, s]) => {
                const entry = { line_item_id: lid, quantity: s.qty, restock: !!s.restock };
                const node = nodeById[lid];
                if (itemsOnly) return entry;   // every line at $0 — the backend books no money
                if (node && node._crossPrice != null) {
                    // Money for this line lives on the original order: add it to the order-level refund
                    // there, and record the line here at $0 (return / restock only).
                    const auto = node._crossPrice * s.qty;
                    crossTotal += computeEffectiveAmount(auto, s.adjust?.mode, s.adjust?.value);
                    crossSummary.push(`${node.sku || node.title || lid} ×${s.qty}`);
                    entry.adjust_mode = 'custom';
                    entry.adjust_value = '0.00';
                    return entry;
                }
                // Send the per-line adjustment so the backend can compute
                // the effective refund amount + book any REFUND_DISCREPANCY
                // orderAdjustment. Absent / mode=full ⇒ auto amount.
                if (s.adjust?.mode && s.adjust.mode !== 'full' && s.adjust.value !== '' && s.adjust.value != null) {
                    entry.adjust_mode  = s.adjust.mode;
                    entry.adjust_value = String(s.adjust.value);
                }
                return entry;
            });
        const body = {
            line_items,
            shipping_refund_amount: shippingChecked && !itemsOnly ? String(Number(shippingAmount).toFixed(2)) : '0.00',
            order_refund_amount: orderRefundChecked && !itemsOnly ? String(Number(orderRefundAmount).toFixed(2)) : '0.00',
            club_discount: itemsOnly ? false : clubDiscount,
            items_only: itemsOnly,
            destination,
            notify_customer: notifyCustomer,
            reason,
            idempotency_key: idempotencyKey,
        };
        const numericOrderId = (order?.id || '').split('/').pop();
        let moneyNote = '';
        if (crossTotal > 0 && parentId) {
            // 1) the money, on the original order
            const parentBody = {
                line_items: [], shipping_refund_amount: '0.00',
                order_refund_amount: crossTotal.toFixed(2),
                club_discount: clubDiscount, destination, notify_customer: notifyCustomer,
                reason: `${reason || 'Refund'} — ${order?.name || 'edited order'}: ${crossSummary.join(', ')}`.slice(0, 255),
                idempotency_key: `${idempotencyKey}-parent`,
            };
            try {
                const pr = await fetch(`/api/orders/${parentId}/refund?store=${encodeURIComponent(storeKey)}`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(parentBody),
                });
                const pd = await pr.json().catch(() => ({}));
                if (!pr.ok) {
                    setErrorBanner({ detail: `Refund on ${parentName || 'the original order'} failed: ${pd.detail || `HTTP ${pr.status}`}. Nothing was refunded.`, retryable: pr.status >= 500 });
                    setSubmitting(false);
                    return;
                }
                moneyNote = `$${crossTotal.toFixed(2)} refunded on ${parentName || 'the original order'}.`;
            } catch (e) {
                setErrorBanner({ detail: `Network error refunding ${parentName || 'the original order'}: ${e.message}. Nothing was refunded.`, retryable: true });
                setSubmitting(false);
                return;
            }
        }
        try {
            // 2) the items (and any own-priced lines / shipping / order amount), on this order
            const r = await fetch(`/api/orders/${numericOrderId}/refund?store=${encodeURIComponent(storeKey)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                if (d.cache_resync_warning) {
                    window.alert(d.cache_resync_warning);
                }
                onRefundIssued?.();
                return;
            }
            setErrorBanner({
                detail: (moneyNote ? `${moneyNote} But recording the items on this order failed: ` : '') + (d.detail || `Refund failed (HTTP ${r.status})`)
                        + (moneyNote ? ' — do NOT submit again (the money is already refunded); record the return in Shopify admin.' : ''),
                retryable: !moneyNote && r.status >= 500,
            });
        } catch (e) {
            setErrorBanner({ detail: (moneyNote ? `${moneyNote} But recording the items here hit a network error: ` : 'Network error: ') + e.message
                             + (moneyNote ? ' — do NOT submit again.' : ''), retryable: !moneyNote });
        } finally {
            setSubmitting(false);
        }
    }

    const allNodes = useMemo(() =>
        (order?.lineItems?.edges || []).map(e => e.node).filter(Boolean),
        [order]
    );

    const liSubtotal = useMemo(() => {
        let s = 0;
        for (const id of Object.keys(selections)) {
            const sel = selections[id];
            if (!sel.selected || !sel.qty) continue;
            const node = allNodes.find(n => n.id === id);
            if (!node) continue;
            const unitPrice = Number(node.originalUnitPriceSet?.shopMoney?.amount || 0);
            const auto = unitPrice * sel.qty;
            s += computeEffectiveAmount(auto, sel.adjust?.mode, sel.adjust?.value);
        }
        return s;
    }, [selections, allNodes]);

    const taxEstimate = useMemo(() => {
        let s = 0;
        for (const id of Object.keys(selections)) {
            const sel = selections[id];
            if (!sel.selected || !sel.qty) continue;
            const node = allNodes.find(n => n.id === id);
            if (!node) continue;
            const lineTax = (node.taxLines || []).reduce(
                (a, t) => a + Number(t.priceSet?.shopMoney?.amount || t.price || 0), 0
            );
            // Tax scales with the effective refund amount. Base ratio is
            // (effective_amount / full_line_total), where full_line_total
            // = unit_price × node.quantity.
            const unitPrice = Number(node.originalUnitPriceSet?.shopMoney?.amount || 0);
            const fullLineTotal = unitPrice * (node.quantity || 1);
            const auto = unitPrice * sel.qty;
            const effectiveAmount = computeEffectiveAmount(auto, sel.adjust?.mode, sel.adjust?.value);
            const proportion = fullLineTotal > 0 ? effectiveAmount / fullLineTotal : 0;
            s += lineTax * proportion;
        }
        return s;
    }, [selections, allNodes]);

    const shippingTotal = shippingChecked ? Math.max(0, Number(shippingAmount) || 0) : 0;
    const orderRefundTotal = orderRefundChecked ? Math.max(0, Number(orderRefundAmount) || 0) : 0;
    const grandTotal = itemsOnly ? 0 : liSubtotal + taxEstimate + shippingTotal + orderRefundTotal;
    const selectedItemCount = Object.values(selections || {}).filter(sel => sel && sel.selected && Number(sel.qty || 0) > 0).length;

    // Ceiling shown at the top of the modal: how much is still refundable on
    // this order = total actually received (paid) minus what's already been
    // refunded. Matches Shopify's "available to refund" figure.
    const _money = m => Number(m?.shopMoney?.amount || 0);
    const ownRefundable = Math.max(0, _money(order?.totalReceivedSet) - _money(order?.totalRefundedSet));
    const parentRefundable = parentOrder ? Math.max(0, _money(parentOrder.totalReceivedSet) - _money(parentOrder.totalRefundedSet)) : 0;
    const maxRefundable = ownRefundable + parentRefundable;
    const crossSelected = (() => {
        let t = 0;
        for (const id of Object.keys(selections)) {
            const sel = selections[id]; if (!sel?.selected || !sel.qty) continue;
            const node = allNodes.find(n => n.id === id); if (!node || node._crossPrice == null) continue;
            t += computeEffectiveAmount(node._crossPrice * sel.qty, sel.adjust?.mode, sel.adjust?.value);
        }
        return t;
    })();

    return (
        <ZoomContext.Provider value={zoomCtx}>
        {zoom && createPortal(<ZoomPopup src={zoom.src} rect={zoom.rect} />, document.body)}
        <Modal
            open
            onClose={onClose}
            title={`Refund ${order?.replacement_alias || order?.name || ''}`}
            size="large"
            primaryAction={{
                // 2026-09-10 (Eric): a $0.00 line (team-kit items priced into the
                // bundle) can still be "refunded" — Shopify records a zero-money
                // refund that returns/restocks the item, i.e. removes it from the
                // order. Enable whenever at least one item is selected.
                content: submitting ? 'Refunding…'
                    : (grandTotal <= 0 && selectedItemCount > 0
                        ? `Remove ${selectedItemCount} item${selectedItemCount === 1 ? '' : 's'} (no refund)`
                        : `Refund $${grandTotal.toFixed(2)}`),
                onAction: handleSubmit,
                disabled: submitting || (grandTotal <= 0 && selectedItemCount === 0),
                loading: submitting,
                destructive: true,
            }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
        >
            {/* Width marker — the only way to scope CSS overrides to this
              * specific Polaris Modal without touching every modal in the app. */}
            <span className="rm-modal-marker" aria-hidden="true" />

            {errorBanner && (
                <Modal.Section>
                    <Banner
                        tone="critical"
                        onDismiss={() => setErrorBanner(null)}
                        action={errorBanner.retryable ? { content: 'Retry', onAction: handleSubmit } : undefined}
                    >
                        <p>{errorBanner.detail}</p>
                    </Banner>
                </Modal.Section>
            )}

            <Modal.Section>
                {parentId && (
                    <Box paddingBlockEnd="400">
                        <Banner tone={parentError ? 'warning' : 'info'} title="This is an edited order">
                            {parentError
                                ? <p>The original order {parentName || parentId} could not be loaded ({parentError}), so prices show as $0.00 here. Refund the money from the original order's page instead.</p>
                                : parentOrder
                                    ? <p>The customer paid ${_money(parentOrder.totalReceivedSet).toFixed(2)} on the original order <b>{parentName || parentOrder.name}</b>
                                        {' '}(${parentRefundable.toFixed(2)} still refundable). Prices below come from that order: the money is refunded there, and the items you pick are
                                        recorded as returned on this order.{crossSelected > 0 ? ` Selected so far: $${crossSelected.toFixed(2)} to refund on ${parentName || parentOrder.name}.` : ''}</p>
                                    : <p>Loading the original order {parentName || parentId}…</p>}
                        </Banner>
                    </Box>
                )}
                {/* Ceiling banner — how much is still refundable on this order
                  * (total received − already refunded), so the operator knows
                  * the max before selecting anything. */}
                <Box paddingBlockEnd="400">
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" fontWeight="medium">Available to refund</Text>
                            <Text as="span" variant="headingMd" fontWeight="semibold">${maxRefundable.toFixed(2)}</Text>
                        </InlineStack>
                    </Box>
                </Box>
                {/* Two-column layout: the product list scrolls the full modal
                  * height on the left; everything else (shipping, totals,
                  * destination, notify, reason) sits in a fixed box on the
                  * right. Stacks back to one column on narrow viewports. */}
                <div className="rm-two-col">
                    <div className="rm-col-products">
                        <BlockStack gap="300">
                            {bundles.length === 0 && standaloneOptionals.length === 0 && standalones.length === 0 && (
                                <Text as="p" tone="subdued">Nothing left to refund on this order.</Text>
                            )}

                            {bundles.map(b => (
                                <BundleCard
                                    key={b.parent.id}
                                    order={order}
                                    bundle={b}
                                    selections={selections}
                                    setSelections={setSelections}
                                />
                            ))}

                            {standaloneOptionals.length > 0 && (
                                <Text variant="headingSm" as="h3">Optional add-ons</Text>
                            )}
                            {standaloneOptionals.map(n => (
                                <ItemCard
                                    key={n.id}
                                    order={order}
                                    node={n}
                                    selections={selections}
                                    setSelections={setSelections}
                                    optionalLabel="Optional add-on"
                                />
                            ))}

                            {standalones.length > 0 && bundles.length + standaloneOptionals.length > 0 && (
                                <Text variant="headingSm" as="h3">Standalone items</Text>
                            )}
                            {standalones.map(n => (
                                <ItemCard
                                    key={n.id}
                                    order={order}
                                    node={n}
                                    selections={selections}
                                    setSelections={setSelections}
                                />
                            ))}
                        </BlockStack>
                    </div>

                    <div className="rm-col-controls">
                        <BlockStack gap="400">
                            {/* Shipping refund — amount always visible so operator
                              * sees the order's shipping cost at a glance. */}
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                                <Checkbox
                                    label="Refund shipping"
                                    checked={shippingChecked && !itemsOnly}
                                    disabled={itemsOnly}
                                    onChange={setShippingChecked}
                                />
                                <Box minWidth="120px" maxWidth="140px">
                                    <TextField
                                        label=""
                                        labelHidden
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prefix="$"
                                        value={String(shippingAmount)}
                                        onChange={v => setShippingAmount(v)}
                                        disabled={!shippingChecked || itemsOnly}
                                        autoComplete="off"
                                    />
                                </Box>
                            </InlineStack>

                            {/* Order-level refund — a discretionary amount tied to
                              * the order, not any specific item. The Club Discount
                              * checkbox sits alongside it: when ticked it forces the
                              * reason to "Club Discount" and tags the order. */}
                            <BlockStack gap="200">
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                    <Checkbox
                                        label="Order refund"
                                        checked={orderRefundChecked && !itemsOnly}
                                        disabled={itemsOnly}
                                        onChange={setOrderRefundChecked}
                                    />
                                    <Box minWidth="96px" maxWidth="110px">
                                        <TextField
                                            label=""
                                            labelHidden
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            prefix="$"
                                            value={String(orderRefundAmount)}
                                            onChange={v => setOrderRefundAmount(v)}
                                            disabled={!orderRefundChecked || itemsOnly}
                                            autoComplete="off"
                                        />
                                    </Box>
                                    <Tooltip
                                        content={'Tags the order with "Club Discount" and will show up on the "Discounts By Club" club report to be invoiced. Typically used if a discount doesn\'t work, or we need to refund for a scholarship player.'}
                                        preferredPosition="above"
                                        dismissOnMouseOut
                                    >
                                        <span style={{ whiteSpace: 'nowrap' }}>
                                            <Checkbox
                                                label="Club Discount"
                                                checked={clubDiscount && !itemsOnly}
                                                disabled={itemsOnly}
                                                onChange={toggleClubDiscount}
                                            />
                                        </span>
                                    </Tooltip>
                                </InlineStack>
                                <Text as="span" tone="subdued" variant="bodySm">
                                    A refund tied to the order, not a specific item.
                                </Text>
                            </BlockStack>

                            <BlockStack gap="100">
                                <Checkbox
                                    label="Don't refund money"
                                    checked={itemsOnly}
                                    onChange={setItemsOnly}
                                    helpText="Removes the ticked items from the order (and restocks them when Restock is ticked) without refunding the customer — e.g. items the club's discount paid for."
                                />
                            </BlockStack>

                            <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                                <BlockStack gap="100">
                                    <InlineStack align="space-between">
                                        <Text as="span" tone="subdued">Subtotal</Text>
                                        <Text as="span">${(itemsOnly ? 0 : liSubtotal).toFixed(2)}</Text>
                                    </InlineStack>
                                    <InlineStack align="space-between">
                                        <Text as="span" tone="subdued">Tax (auto)</Text>
                                        <Text as="span">${(itemsOnly ? 0 : taxEstimate).toFixed(2)}</Text>
                                    </InlineStack>
                                    <InlineStack align="space-between">
                                        <Text as="span" tone="subdued">Shipping</Text>
                                        <Text as="span">${(itemsOnly ? 0 : shippingTotal).toFixed(2)}</Text>
                                    </InlineStack>
                                    {orderRefundTotal > 0 && !itemsOnly && (
                                        <InlineStack align="space-between">
                                            <Text as="span" tone="subdued">Order refund</Text>
                                            <Text as="span">${orderRefundTotal.toFixed(2)}</Text>
                                        </InlineStack>
                                    )}
                                    <Divider />
                                    <InlineStack align="space-between">
                                        <Text as="span" fontWeight="semibold">Refund total</Text>
                                        <Text as="span" fontWeight="semibold">${grandTotal.toFixed(2)}</Text>
                                    </InlineStack>
                                </BlockStack>
                            </Box>

                            <BlockStack gap="200">
                                <Text variant="bodyMd" fontWeight="medium" as="span">Issue refund to</Text>
                                <RadioButton
                                    label="Original payment method"
                                    checked={destination === 'original_payment'}
                                    disabled={itemsOnly}
                                    onChange={() => setDestination('original_payment')}
                                    name="rm-destination"
                                />
                                <RadioButton
                                    label={customerHasAccount ? 'Store credit' : 'Store credit (customer has no account)'}
                                    checked={destination === 'store_credit'}
                                    disabled={!customerHasAccount || itemsOnly}
                                    onChange={() => setDestination('store_credit')}
                                    name="rm-destination"
                                />
                            </BlockStack>

                            <Checkbox
                                label="Notify customer by email"
                                checked={notifyCustomer}
                                onChange={setNotifyCustomer}
                            />

                            {/* Multiline reason — matches the styled box pattern
                              * Shopify uses on its native refund modal. Locked to
                              * "Club Discount" while that box is ticked. */}
                            <TextField
                                label="Reason for refund"
                                helpText={clubDiscount
                                    ? 'Set to "Club Discount" — uncheck Club Discount to edit'
                                    : 'Only visible to staff — not sent to the customer'}
                                value={reason}
                                onChange={setReason}
                                disabled={clubDiscount}
                                multiline={3}
                                autoComplete="off"
                            />
                        </BlockStack>
                    </div>
                </div>
            </Modal.Section>
        </Modal>
        </ZoomContext.Provider>
    );
}
